import { Role } from "../auth/types/authType";
import { RoleTypes } from "../auth/types/roleTypes";
import {
  NotificationAudience,
  NotificationMessageTypes,
} from "../process/types/processTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";

/** Hidden from navbar popup, badge count, and browser push. */
export const POPUP_EXCLUDED_MESSAGES: string[] = [
  NotificationMessageTypes.updated,
  NotificationMessageTypes.first_timer_added,
];

export function shouldShowInPopup(message: string): boolean {
  return !POPUP_EXCLUDED_MESSAGES.includes(message);
}

const MODULE_TO_AUDIENCE: Record<string, NotificationAudience> = {
  Records: NotificationAudience.records,
  ProcessControl: NotificationAudience.pipeline,
  Wall: NotificationAudience.wall,
};

const NOTIFICATIONS_PATH = "/app/notifications";

function moduleOf(displayName: string): string {
  return displayName.includes(".")
    ? displayName.slice(0, displayName.indexOf("."))
    : displayName;
}

function employeeIdOf(
  employee?: { _id?: string } | string | null,
): string | undefined {
  if (!employee) return undefined;
  if (typeof employee === "string") {
    const id = employee.slice(0, 24);
    return id || undefined;
  }
  return employee._id || undefined;
}

/** Audiences the current user may view (empty = none). */
export function getUserNotificationAudiences(
  roles: Role[],
): NotificationAudience[] {
  if (isPrivilegedTransaction(roles, RoleTypes.ManageUsers)) {
    const audiences = [
      NotificationAudience.records,
      NotificationAudience.pipeline,
    ];
    if (isPrivilegedTransaction(roles, RoleTypes.ViewWall)) {
      audiences.push(NotificationAudience.wall);
    }
    return audiences;
  }

  const audiences = new Set<NotificationAudience>();
  for (const role of roles) {
    const name = role.displayName ?? "";
    const aud = MODULE_TO_AUDIENCE[moduleOf(name)];
    if (aud) audiences.add(aud);
  }
  return Array.from(audiences);
}

export function hasNotificationAudience(
  roles: Role[],
  audience: NotificationAudience,
): boolean {
  return getUserNotificationAudiences(roles).includes(audience);
}

export function notificationLink(notification: {
  wallPost?: { _id?: string };
  employee?: { _id?: string } | string | null;
}): string {
  const wallPostId = notification.wallPost?._id;
  if (wallPostId) {
    return `/app/wall?postId=${wallPostId}`;
  }

  const employeeId = employeeIdOf(notification.employee);
  if (employeeId) {
    return `/app/employee-detail/${employeeId}`;
  }

  return NOTIFICATIONS_PATH;
}

export { NotificationAudience };
